import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { MapPin, Shield, Truck } from 'lucide-react';
import heroImage from '@/assets/hero-mountain.jpg';

export function HeroSection() {
  const { t } = useTranslation();
  
  return (
    <section className="relative h-screen min-h-[700px] flex items-center justify-center overflow-hidden -mt-[84px] md:-mt-[132px] pt-[84px] md:pt-[132px]">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img src={heroImage} alt="Handcrafted bronze mountain jewelry by Sampaoli - Italian Dolomites backdrop" className="w-full h-full object-cover" fetchPriority="high" width={1920} height={1080} />
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <p 
            className="font-elegant text-lg md:text-xl lg:text-2xl text-white italic mb-4 tracking-widest"
            style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}
          >
            {t('hero.elegantSubtitle')}
          </p>
          <h1 
            className="font-display text-2xl md:text-4xl lg:text-5xl text-cream uppercase tracking-[0.15em] mb-10"
            style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.4)' }}
          >
            {t('hero.tagline')}
          </h1>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }} 
          className="flex flex-row gap-3 justify-center"
        >
          <Button variant="hero" size="default" className="text-xs md:text-sm px-4 md:px-6 py-2 md:py-3" asChild>
            <Link to="/collections">{t('hero.cta')}</Link>
          </Button>
          <Button variant="heroOutline" size="default" className="text-xs md:text-sm px-4 md:px-6 py-2 md:py-3" asChild>
            <Link to="/about">{t('hero.secondary')}</Link>
          </Button>
        </motion.div>
      </div>

      {/* Features Banner */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute bottom-0 left-0 right-0 z-10"
      >
        <div className="bg-charcoal/80 backdrop-blur-sm border-t border-bronze/20">
          <div className="container mx-auto px-2 md:px-4 py-2 md:py-3">
            <div className="flex flex-row items-center justify-center gap-3 md:gap-12 text-center">
              <div className="flex items-center gap-1 md:gap-2">
                <MapPin className="w-3 h-3 md:w-4 md:h-4 text-bronze flex-shrink-0" />
                <div className="text-left">
                  <span className="text-cream text-[10px] md:text-xs font-medium uppercase tracking-wider">{t('banner.madeInItaly')}</span>
                  <p className="text-cream/60 text-[8px] md:text-[10px] hidden sm:block">{t('banner.madeInItalyDesc')}</p>
                </div>
              </div>
              <div className="hidden md:block w-px h-8 bg-bronze/30" />
              <div className="flex items-center gap-1 md:gap-2">
                <Shield className="w-3 h-3 md:w-4 md:h-4 text-bronze flex-shrink-0" />
                <div className="text-left">
                  <span className="text-cream text-[10px] md:text-xs font-medium uppercase tracking-wider">{t('banner.hypoallergenic')}</span>
                  <p className="text-cream/60 text-[8px] md:text-[10px] hidden sm:block">{t('banner.hypoallergenicDesc')}</p>
                </div>
              </div>
              <div className="hidden md:block w-px h-8 bg-bronze/30" />
              <div className="flex items-center gap-1 md:gap-2">
                <Truck className="w-3 h-3 md:w-4 md:h-4 text-bronze flex-shrink-0" />
                <div className="text-left">
                  <span className="text-cream text-[10px] md:text-xs font-medium uppercase tracking-wider">{t('banner.globalShipping')}</span>
                  <p className="text-cream/60 text-[8px] md:text-[10px] hidden sm:block">{t('banner.globalShippingDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}